import type { App } from '../app/App';
import { t } from '../i18n';
import { h } from './dom';

/**
 * Ink Strike: a quick trace-the-shape mini-game after some won rounds (single
 * player). The better the trace, the bigger the extra hit (combat/twists.ts
 * TWISTS.trace). Shapes and scoring work in a 0..1 square.
 */

type Pt = [number, number];

function ring(n: number, r: number, cx = 0.5, cy = 0.5): Pt[] {
  return Array.from({ length: n + 1 }, (_, i) => {
    const a = -Math.PI / 2 + (i / n) * Math.PI * 2;
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r] as Pt;
  });
}

/** The guide shapes as polylines. */
export const TRACE_SHAPES: Record<string, Pt[]> = {
  circle: ring(36, 0.34),
  zigzag: [[0.12, 0.7], [0.3, 0.3], [0.5, 0.7], [0.7, 0.3], [0.88, 0.7]],
  wave: Array.from({ length: 31 }, (_, i) => [0.1 + (i / 30) * 0.8, 0.5 + Math.sin((i / 30) * Math.PI * 2) * 0.22] as Pt),
  star: Array.from({ length: 11 }, (_, i) => {
    const a = -Math.PI / 2 + (i / 10) * Math.PI * 2;
    const r = i % 2 === 0 ? 0.38 : 0.16;
    return [0.5 + Math.cos(a) * r, 0.52 + Math.sin(a) * r] as Pt;
  }),
};

/** Points along a polyline every `step` units. */
export function resample(line: readonly Pt[], step: number): Pt[] {
  const out: Pt[] = [line[0]!];
  for (let i = 1; i < line.length; i++) {
    const [x0, y0] = line[i - 1]!;
    const [x1, y1] = line[i]!;
    const n = Math.max(1, Math.round(Math.hypot(x1 - x0, y1 - y0) / step));
    for (let k = 1; k <= n; k++) out.push([x0 + ((x1 - x0) * k) / n, y0 + ((y1 - y0) * k) / n]);
  }
  return out;
}

const nearest = (p: Pt, pts: readonly Pt[]): number => pts.reduce((best, q) => Math.min(best, Math.hypot(p[0] - q[0], p[1] - q[1])), Infinity);

/**
 * 0..1: how much of the guide was covered (within `near`) times how much of
 * the drawing stayed on it (within 1.5 × `near`), so scribbling everywhere
 * scores as badly as drawing too little.
 */
export function traceScore(guide: readonly Pt[], drawn: readonly Pt[], near = 0.07): number {
  if (drawn.length < 2) return 0;
  const g = resample(guide, 0.02);
  const d = resample(drawn, 0.02);
  const covered = g.filter((p) => nearest(p, d) <= near).length / g.length;
  const onLine = d.filter((p) => nearest(p, g) <= near * 1.5).length / d.length;
  return Math.round(covered * onLine * 100) / 100;
}

/** Shows the mini-game and resolves with the score (0..1) when time is up or the stroke is finished. */
export function traceGame(app: App, seconds: number): Promise<number> {
  const names = Object.keys(TRACE_SHAPES);
  const shape = TRACE_SHAPES[names[Math.floor(Math.random() * names.length)]!]!;
  const size = 320;
  const canvas = h('canvas', { class: 'trace-canvas', width: size * 2, height: size * 2, 'aria-label': t('Trace the dotted shape') }) as HTMLCanvasElement;
  const bar = h('span', { class: 'trace-time-fill' });
  const verdict = h('p', { class: 'trace-verdict', 'aria-live': 'polite' });
  const card = h('div', { class: 'trace-card paper-scrap', role: 'dialog', 'aria-label': t('Ink Strike') },
    h('h3', { class: 'trace-title' }, '✏️ ', t('Ink Strike!')),
    h('p', { class: 'trace-hint' }, t('Trace the dotted shape in one go: the neater, the harder you hit!')),
    canvas,
    h('div', { class: 'trace-time' }, bar),
    verdict,
  );
  const wrap = h('div', { class: 'trace-wrap' }, card);
  document.getElementById('overlay-layer')?.appendChild(wrap);
  const ctx = canvas.getContext('2d')!;
  const px = (p: Pt): Pt => [p[0] * canvas.width, p[1] * canvas.height];
  const drawGuide = (): void => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.setLineDash([10, 14]);
    ctx.lineWidth = 10;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#8e5bd655';
    ctx.beginPath();
    shape.forEach((p, i) => (i ? ctx.lineTo(...px(p)) : ctx.moveTo(...px(p))));
    ctx.stroke();
    ctx.restore();
    // Where to start.
    const [sx, sy] = px(shape[0]!);
    ctx.fillStyle = '#6ab04c';
    ctx.beginPath();
    ctx.arc(sx, sy, 14, 0, Math.PI * 2);
    ctx.fill();
  };
  drawGuide();

  return new Promise((resolve) => {
    const drawn: Pt[] = [];
    let pointer: number | null = null;
    let done = false;
    const start = performance.now();
    const at = (e: PointerEvent): Pt => {
      const r = canvas.getBoundingClientRect();
      return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))];
    };
    const finish = (): void => {
      if (done) return;
      done = true;
      cancelAnimationFrame(frame);
      const score = traceScore(shape, drawn);
      verdict.textContent = score >= 0.8 ? t('Perfect line! {n}%', { n: Math.round(score * 100) }) : score >= 0.25 ? t('Nice! {n}%', { n: Math.round(score * 100) }) : t('Missed… {n}%', { n: Math.round(score * 100) });
      card.classList.add(score >= 0.8 ? 'great' : score >= 0.25 ? 'ok' : 'miss');
      app.sfx.play(score >= 0.8 ? 'crit' : score >= 0.25 ? 'scribble' : 'miss');
      window.setTimeout(() => {
        wrap.remove();
        resolve(score);
      }, 650);
    };
    canvas.addEventListener('pointerdown', (e) => {
      if (done || pointer !== null) return;
      e.preventDefault();
      pointer = e.pointerId;
      canvas.setPointerCapture(e.pointerId);
      drawn.push(at(e));
    });
    canvas.addEventListener('pointermove', (e) => {
      if (e.pointerId !== pointer || done) return;
      const p = at(e);
      const last = drawn[drawn.length - 1]!;
      drawn.push(p);
      ctx.strokeStyle = '#2e86de';
      ctx.lineWidth = 12;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(...px(last));
      ctx.lineTo(...px(p));
      ctx.stroke();
    });
    const up = (e: PointerEvent): void => {
      if (e.pointerId !== pointer) return;
      pointer = null;
      // One stroke: lifting the pen ends the strike.
      if (drawn.length > 3) finish();
      else drawn.length = 0;
    };
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    let frame = 0;
    const tick = (): void => {
      const left = 1 - (performance.now() - start) / (seconds * 1000);
      bar.style.width = `${Math.max(0, left) * 100}%`;
      if (left <= 0) finish();
      else frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
  });
}
